import React, { useEffect } from 'react';

import SkuStore from '@/stores/sku';
import PageStore from '@/stores/page';
import DrawerStore from '@/stores/drawer';
import NoticeStore from '@/stores/notice';

import ClassNames from 'classnames';
import localFormat from '@/common/local-format';
import roundNumber from '@/common/round-number';
import doLog from '@/common/log';

import NumberSelect from './numberSelect';
import InventoryBadge from './inventory-badge';
interface AvailableActivity {
  activityId: number;
  endTime: number;
  hasLimit: boolean;
  limitCount: number;
  promotionContent: string;
  startTime: number;
  status: string;
}
interface PropTypes {
  isLast: boolean;
  skuId: number;
  item: SkuTypes.Value;
  moq: number;
  index: number;
  price: PriceTypes.Root;
  attrIndex: number;
  moduleName: string;
  i18n: DetailTypes.i18n;
  renderConfig: DetailTypes.renderConfig<{
    showType: string;
  }>;
  type: SkuTypes.TypeInfo;
  availableActivity: AvailableActivity;
}

export default function Item(props: PropTypes) {
  const { isLast, item, type, price, moq, renderConfig, index, i18n, availableActivity } = props;
  const { showType } = renderConfig;
  const skuStore = SkuStore((state) => state);
  const noticeStore = NoticeStore((state) => state);

  const secondScreen = PageStore((state) => state.secondScreen);
  const { dialogShow } = DrawerStore((state) => state);
  const skuInfo = skuStore[type];
  const { selectedKey, mapInfo, promotionPrice, price: productPrice, isSoldOut } = skuInfo;
  const {
    productLadderPrices = [],
    currencyRule,
    formatFixedPrice,
    formatPromotionFixedPrice,
    unitEven,
    unit,
    batchNum,
  } = price;
  const { maximumFractionDigits, rate } = currencyRule;
  // 营销活动限购数量
  const limitCount = availableActivity?.limitCount;

  // 获取key
  const keyArray = selectedKey.split(':').slice(0, -1);
  keyArray.push(`${item.id}`);
  const keyString = keyArray.join(':');

  //当加购超出限购限制时出notice提示
  useEffect(() => {
    if (!limitCount) return;
    if (limitCount <= skuInfo.quantity) {
      noticeStore.modify(
        i18n['sku_purchase_limit_tips'].replace(
          '{0}',
          limitCount + ' ' + (limitCount > 1 ? unitEven : unit),
        ),
      );
    }
  }, [mapInfo[keyString]]);

  // skuInfo.quantity是sku各项加起来选择的数量，quantity是当前项的数量
  const getMax = (maxQuantity: number, inventoryCount: number, quantity: number) => {
    // 样品需要对数量进行控制
    if (type === 'sample' && inventoryCount > 0) {
      // 库存
      if (inventoryCount <= maxQuantity) return inventoryCount;
      // 有货
      return quantity - (skuInfo.quantity - maxQuantity);
    }

    if (type === 'product' && limitCount) {
      // 库存比limitCount还少
      if (inventoryCount <= limitCount && skuInfo.quantity < limitCount) return inventoryCount;
      return quantity - (skuInfo.quantity - limitCount);
    }
    return inventoryCount;
  };

  const getPrice = (normalPrice: number) => {
    if (normalPrice) {
      return localFormat(roundNumber(normalPrice * rate, maximumFractionDigits));
    }
    if (productLadderPrices.length > 0) {
      return productLadderPrices[0].formatPromotionPrice || productLadderPrices[0].formatPrice;
    }
    if (formatFixedPrice) {
      return formatPromotionFixedPrice || formatFixedPrice;
    }
  };

  const renderSkuItems = () => {
    return (
      <>
        {/* 引入徽章组件 */}
        {index === 0 && showType !== 'sku' && !isLast && (
          <InventoryBadge mapInfo={mapInfo} item={item} />
        )}
        {item.type.toLocaleLowerCase() === 'image' && (
          <img key={item.id} src={item.smallImage} loading={'lazy'} />
        )}
        {item.type.toLocaleLowerCase() === 'text' && <span>{item.name}</span>}
      </>
    );
  };
  /** 库存提示文案
   *  moq>库存  出out of stock
      moq <=5，moq<=库存 <=15 出only xx left 
      moq > 5, 库存/moq <=2  出only xx left 
   */
  const renderInventoryInfo = (inventoryCount: number) => {
    // 非sold out才出库存提示
    if (isSoldOut) return null;
    // 库存文案
    let inventoryMessage = null;
    //  inventoryMoq主商品与样品moq不一致
    const inventoryMoq = type === 'sample' ? 1 : moq;
    // 库存单元
    const inventoryUnit = inventoryCount > 1 ? unitEven : unit;
    // 按批次的库存数据需要特殊处理，向下按批次取整
    const adjustedInventoryCount =
      batchNum !== 1 && type === 'product'
        ? Math.floor(inventoryCount / batchNum) * batchNum
        : inventoryCount;
    // showSoldOut为true时，透出 sold out 文案
    const showSoldOut =
      adjustedInventoryCount === 0 || (batchNum !== 1 && adjustedInventoryCount < inventoryMoq);
    // showInventoryCount为true时，透出 only xx left 文案
    const showInventoryCount =
      (inventoryMoq <= 5 && adjustedInventoryCount <= 15) ||
      (inventoryMoq > 5 && adjustedInventoryCount / inventoryMoq <= 2);

    if (showSoldOut) {
      inventoryMessage = (
        <div className="inventory-info out-of-stock">{i18n['pc_detail_detail_stock_sold_out']}</div>
      );
    } else if (showInventoryCount) {
      inventoryMessage = (
        <div className="inventory-info tight-stock">
          {i18n['detail_detail_stock_less_stock_left']
            ?.replace('{0}', `${adjustedInventoryCount}`)
            ?.replace('{1}', inventoryUnit)}
        </div>
      );
    }

    return inventoryMessage;
  };

  /**
   * 渲染最后一阶
   * 最后一阶需要展示价格，分两种情况
   * 1、出浮层
   * 2、不出浮层：最后一项sku在8个以内
   */
  if (isLast && showType !== 'sku') {
    if (mapInfo[keyString]) {
      const {
        inventoryCount,
        maxQuantity,
        quantity = 0,
        formatPrice,
        formatPromotionPrice,
      } = mapInfo[keyString];

      return (
        <div className="last-sku-item">
          <div
            className={ClassNames({
              number: true,
              active: skuInfo?.skuActiveId === item.id,
            })}
            onClick={(e) => {
              e.preventDefault();
              // if (item.type.toLocaleLowerCase() === 'image')
              skuStore.setSkuImageReview(type, item, showType);
              // else skuStore.setSkuImageReview(type, item);
            }}
          >
            <span className="last-sku-first-item">
              <span
                className={item.type.toLocaleLowerCase()}
                style={{
                  background: 'color' === item.type.toLocaleLowerCase() ? item.color : '',
                  marginRight: '4px',
                }}
              >
                {/* 渲染每一阶sku的具体每一项 */}
                {renderSkuItems()}
              </span>
              {(item.type.toLocaleLowerCase() === 'image' ||
                item.type.toLocaleLowerCase() === 'color') && <span>{item.name}</span>}
            </span>
            <span className="price">
              {formatPromotionPrice ||
                formatPrice ||
                getPrice(Number(promotionPrice || productPrice))}
            </span>
            <NumberSelect
              i18n={i18n}
              min={0}
              max={getMax(maxQuantity, inventoryCount, quantity)}
              type={type}
              rule={price}
              moq={moq}
              skuKey={keyString}
              quantity={quantity}
              limitCount={limitCount}
            />
          </div>
          {/* 出库存提示 */}
          {renderInventoryInfo(inventoryCount)}
        </div>
      );
    }
  }
  if (isLast && !mapInfo[keyString]) return null;

  /**
   * 渲染非最后一阶
   */
  return (
    <a
      href="#"
      key={item.id}
      style={{
        background: 'color' === item.type.toLocaleLowerCase() ? item.color : '',
      }}
      onClick={(e) => {
        e.preventDefault();
        doLog('/sc.details.skuArea', `second=${secondScreen}&from=select`, 'EXP');
        if (showType === 'sku') {
          dialogShow({
            type,
          });
        }
        skuStore.setSkuSelect(type, item);
        skuStore.setSkuImageReview(type, item, showType);
        // skuStore.setIsSkuImage(type, item);
      }}
      className={ClassNames({
        [item.type.toLocaleLowerCase()]: true,
        selected: item.selected === true && !isLast,
      })}
    >
      {renderSkuItems()}
    </a>
  );
}
